import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Star, Play, Film, Heart, Bookmark, Sparkles, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLists } from "@/contexts/ListsContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function CollectionPage() {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isIn, toggle } = useLists();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true); setErr(null);
        const { data: d } = await api.get(`/collections/${collectionId}`);
        if (!cancel) setData(d);
      } catch (e) {
        if (!cancel) setErr(e.response?.data?.detail || "Collection not found");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [collectionId]);

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header />

        {/* Backdrop hero */}
        {data?.backdrop && (
          <div className="relative w-full h-64 md:h-80 overflow-hidden">
            <img src={data.backdrop} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
          </div>
        )}

        <main className="px-4 md:px-8 py-6 flex-1 max-w-6xl mx-auto w-full -mt-32 relative z-10">
          <Link
            to="/"
            data-testid="collection-back-home"
            className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-2 text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          {loading ? (
            <div className="flex items-center gap-2 text-[var(--tt-text-muted)] py-12">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading collection…
            </div>
          ) : err ? (
            <div className="text-center text-[var(--tt-text-muted)] py-16 text-sm">
              {err}
            </div>
          ) : data && (
            <>
              <header className="mb-8 flex flex-col md:flex-row gap-6 items-start">
                {data.poster && (
                  <img
                    src={data.poster}
                    alt={data.name}
                    className="w-32 md:w-44 rounded-xl shadow-2xl shadow-black/60 border border-[var(--tt-border)] flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--tt-yellow)] mb-2 flex items-center gap-2">
                    <Film className="w-3.5 h-3.5" /> Movie Collection
                  </div>
                  {data.logo ? (
                    <img
                      src={data.logo}
                      alt={data.name}
                      data-testid="collection-logo"
                      className="max-h-24 md:max-h-32 w-auto max-w-full object-contain drop-shadow-[0_4px_20px_rgba(0,0,0,0.7)]"
                    />
                  ) : (
                    <h1
                      data-testid="collection-name"
                      className="font-display text-3xl md:text-5xl font-extrabold leading-tight"
                    >
                      {data.name}
                    </h1>
                  )}
                  <div className="text-sm text-[var(--tt-text-muted)] mt-2 font-mono-tt">
                    {data.parts.length} {data.parts.length === 1 ? "film" : "films"}
                  </div>

                  {user && (() => {
                    const favCount = data.parts.filter((p) => isIn("favorites", p.id, "movie")).length;
                    const wlCount = data.parts.filter((p) => isIn("watchlist", p.id, "movie")).length;
                    return (
                      <div
                        data-testid="saga-progress"
                        className="mt-4 flex items-center gap-4 flex-wrap"
                      >
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--tt-surface)] border border-[var(--tt-border)]">
                          <Heart
                            className={`w-3.5 h-3.5 ${favCount > 0 ? "text-[var(--tt-primary)] fill-current" : "text-[var(--tt-text-muted)]"}`}
                          />
                          <span className="text-xs font-mono-tt">
                            <span className="font-bold text-[var(--tt-text)]">{favCount}</span>
                            <span className="text-[var(--tt-text-muted)]"> / {data.parts.length} favorited</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--tt-surface)] border border-[var(--tt-border)]">
                          <Bookmark
                            className={`w-3.5 h-3.5 ${wlCount > 0 ? "text-[var(--tt-yellow)] fill-current" : "text-[var(--tt-text-muted)]"}`}
                          />
                          <span className="text-xs font-mono-tt">
                            <span className="font-bold text-[var(--tt-text)]">{wlCount}</span>
                            <span className="text-[var(--tt-text-muted)]"> / {data.parts.length} on watchlist</span>
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {data.overview && (
                    <p className="text-sm text-[var(--tt-text)] mt-4 leading-relaxed max-w-3xl">
                      {data.overview}
                    </p>
                  )}
                </div>
              </header>

              {user && (() => {
                const tracked = data.parts.filter(
                  (p) => isIn("favorites", p.id, "movie") || isIn("watchlist", p.id, "movie")
                );
                const missing = data.parts.filter(
                  (p) => !isIn("favorites", p.id, "movie") && !isIn("watchlist", p.id, "movie")
                );
                if (tracked.length === 0 || missing.length === 0 || missing.length > 2) return null;
                const headline = missing.length === 1
                  ? `You're 1 film away from completing ${data.name}`
                  : `You're 2 films away from completing ${data.name}`;
                return (
                  <div
                    data-testid="complete-saga-cta"
                    className="mb-8 relative overflow-hidden rounded-2xl border border-[var(--tt-primary)]/40 bg-gradient-to-br from-[var(--tt-primary)]/10 via-black to-[var(--tt-purple)]/10 p-5"
                  >
                    <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-[var(--tt-primary)]/20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-primary)] mb-1.5">
                          <Sparkles className="w-3.5 h-3.5" /> Complete The Saga
                        </div>
                        <div className="font-display text-lg md:text-xl font-extrabold">
                          {headline}
                        </div>
                        <div className="text-xs text-[var(--tt-text-muted)] mt-1">
                          Finish {missing.map((p) => p.title).join(" and ")} to lock the full collection.
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {missing.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`complete-saga-go-${p.id}`}
                            onClick={() => navigate(`/title/movie/${p.id}`)}
                            className="group flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--tt-primary)]/15 hover:bg-[var(--tt-primary)]/25 border border-[var(--tt-primary)]/40 transition-colors"
                          >
                            {p.poster && (
                              <img
                                src={p.poster}
                                alt={p.title}
                                className="w-7 h-10 rounded object-cover"
                              />
                            )}
                            <div className="text-left min-w-0">
                              <div className="text-[11px] uppercase tracking-wider text-[var(--tt-primary)] font-bold">
                                Finish
                              </div>
                              <div className="text-xs font-bold truncate max-w-[140px]">
                                {p.title}
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-[var(--tt-primary)] group-hover:translate-x-0.5 transition-transform" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

              <section>
                <h2 className="text-[11px] uppercase tracking-wider text-[var(--tt-text-muted)] mb-4">
                  Films · Chronological
                </h2>
                <div
                  data-testid="collection-parts"
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5"
                >
                  {data.parts.map((p) => {
                    const fav = user ? isIn("favorites", p.id, "movie") : false;
                    const wl = user ? isIn("watchlist", p.id, "movie") : false;
                    const toggleList = async (e, listType) => {
                      e.stopPropagation();
                      if (!user) {
                        toast.error("Sign in to track your collection progress");
                        return;
                      }
                      try {
                        const added = await toggle(listType, {
                          tmdb_id: p.id,
                          media_type: "movie",
                          title: p.title,
                          year: p.year,
                          poster: p.poster,
                          backdrop: p.backdrop,
                          rating: p.rating,
                          overview: p.overview,
                        });
                        toast.success(
                          added
                            ? `${p.title} added to ${listType === "favorites" ? "favorites" : "watchlist"}`
                            : `${p.title} removed from ${listType === "favorites" ? "favorites" : "watchlist"}`
                        );
                      } catch {
                        toast.error("Couldn't update list");
                      }
                    };
                    return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`collection-part-${p.id}`}
                      onClick={() => navigate(`/title/movie/${p.id}`)}
                      className="group text-left"
                    >
                      <div className="relative aspect-[2/3] rounded-xl overflow-hidden bg-[var(--tt-surface)] border border-[var(--tt-border)] hover:border-[var(--tt-primary)] transition-colors">
                        {p.poster ? (
                          <img
                            src={p.poster}
                            alt={p.title}
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-[var(--tt-text-muted)] p-2 text-center">
                            {p.title}
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                          <span className="flex items-center gap-1.5 text-xs font-bold text-white">
                            <Play className="w-3.5 h-3.5 fill-current" /> Open torrents
                          </span>
                        </div>
                        {p.rating > 0 && (
                          <div className="absolute top-2 right-2 flex items-center gap-0.5 text-[10px] font-bold bg-black/70 backdrop-blur px-1.5 py-0.5 rounded">
                            <Star className="w-2.5 h-2.5 text-[var(--tt-yellow)] fill-current" />
                            {p.rating}
                          </div>
                        )}
                        {/* Favorite / Watchlist toggles */}
                        <div className="absolute top-2 left-2 flex flex-col gap-1.5">
                          <button
                            type="button"
                            data-testid={`collection-fav-${p.id}`}
                            onClick={(e) => toggleList(e, "favorites")}
                            aria-label="Toggle favorite"
                            className={`w-7 h-7 rounded-full backdrop-blur flex items-center justify-center border transition-all ${
                              fav
                                ? "bg-[var(--tt-primary)]/90 border-[var(--tt-primary)] text-white shadow-[0_0_10px_rgba(255,42,109,0.5)]"
                                : "bg-black/60 border-white/20 text-white/70 hover:text-white hover:border-white/50"
                            }`}
                          >
                            <Heart className={`w-3.5 h-3.5 ${fav ? "fill-current" : ""}`} />
                          </button>
                          <button
                            type="button"
                            data-testid={`collection-wl-${p.id}`}
                            onClick={(e) => toggleList(e, "watchlist")}
                            aria-label="Toggle watchlist"
                            className={`w-7 h-7 rounded-full backdrop-blur flex items-center justify-center border transition-all ${
                              wl
                                ? "bg-[var(--tt-yellow)]/90 border-[var(--tt-yellow)] text-black shadow-[0_0_10px_rgba(245,200,66,0.5)]"
                                : "bg-black/60 border-white/20 text-white/70 hover:text-white hover:border-white/50"
                            }`}
                          >
                            <Bookmark className={`w-3.5 h-3.5 ${wl ? "fill-current" : ""}`} />
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 px-0.5">
                        <div className="text-sm font-bold truncate group-hover:text-[var(--tt-primary)] transition-colors">
                          {p.title}
                        </div>
                        {p.year && (
                          <div className="text-[11px] text-[var(--tt-text-muted)] font-mono-tt">
                            {p.year}
                          </div>
                        )}
                      </div>
                    </button>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </main>
        <Footer />
      </div>
    </div>
  );
}
